// Whose progress this device's mirror is (ticket 219). Signing in binds the device's Academy progress
// to the account without a question: the progress is claimed into the account and synced through the
// account's routes from then on. What another account left on a shared device is set aside under that
// account's id and given back when it signs in again; it is never merged into anyone else's.
import type { AuthState } from "../social/auth";

const KEY = "proef.learn.account";

export interface Binding {
  readonly ownerId: string;
  /** True once the device's progress was claimed into the account and the account routes are in use. */
  readonly claimed: boolean;
}

export function readBinding(): Binding | undefined {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (raw === null) return undefined;
    const value: unknown = JSON.parse(raw);
    if (typeof value !== "object" || value === null) return undefined;
    const ownerId = "ownerId" in value ? value.ownerId : undefined;
    if (typeof ownerId !== "string") return undefined;
    // An earlier build stored a choice; "bring" was a claim made, "decline" one still to make.
    const claimed =
      "claimed" in value ? value.claimed === true : "choice" in value && value.choice === "bring";
    return { ownerId, claimed };
  } catch {
    return undefined;
  }
}

export function writeBinding(binding: Binding | undefined): void {
  try {
    if (binding === undefined) window.localStorage.removeItem(KEY);
    else window.localStorage.setItem(KEY, JSON.stringify(binding));
  } catch {
    // Storage blocked: the device keeps syncing per device, which loses nothing.
  }
}

/** The account's routes only for the account the progress was claimed into. */
export function routeFor(auth: AuthState, binding: Binding | undefined): "account" | "device" {
  return auth.status === "signedIn" && binding?.claimed === true && binding.ownerId === auth.user.id
    ? "account"
    : "device";
}

/** Where one account's set-aside Academy data waits on this device. */
export const parkedPrefix = (ownerId: string): string => `parked:${ownerId}:`;
export const LIVE_PREFIX = "user:learn:";
